// ============================================
// Reconciliation Page
// Match Plaid bank transactions to Payments/Expenses
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useMessage } from '../contexts/MessageContext';
import {
  Box, Button, Typography, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel,
  Tooltip, IconButton, Alert
} from '@mui/material';
import {
  AutoFixHigh, LinkOff, CheckCircle, VisibilityOff,
  Add, ArrowForward, CompareArrows, Refresh
} from '@mui/icons-material';

export default function Reconciliation() {
  const { showMessage } = useMessage();

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTxns, setTotalTxns] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0=unmatched, 1=matched, 2=ignored
  const [loading, setLoading] = useState(true);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Match dialog
  const [matchDialog, setMatchDialog] = useState(null); // { txn, type: 'payment'|'expense' }
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Create expense dialog
  const [createExpenseDialog, setCreateExpenseDialog] = useState(null); // txn
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const statusFilters = ['unmatched', 'matched', 'ignored', 'all'];

  // ── Load data ──
  const loadSummary = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/reconciliation/summary');
      if (res.data.success) setSummary(res.data.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, []);

  const loadTransactions = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const status = statusFilters[activeTab];
      const offset = p * ps;
      const res = await axiosInstance.get(
        `/reconciliation/transactions?status=${status}&limit=${ps}&offset=${offset}`
      );
      if (res.data.success) {
        setTransactions(res.data.data.transactions || []);
        setTotalTxns(res.data.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, pageSize]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/account');
      if (res.data.success) {
        const expenseAccounts = (res.data.data || []).filter(
          a => a.type?.toLowerCase().includes('expense')
        );
        setExpenseCategories(expenseAccounts);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadCategories();
  }, [loadSummary, loadCategories]);

  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  useEffect(() => {
    loadTransactions(page, pageSize);
  }, [page, pageSize, activeTab]);

  // ── Actions ──
  const handleAutoMatch = async () => {
    setAutoMatchLoading(true);
    try {
      const res = await axiosInstance.post('/reconciliation/auto-match');
      if (res.data.success) {
        showMessage('success', res.data.message);
        loadTransactions(0, pageSize);
        loadSummary();
        setPage(0);
      }
    } catch (err) {
      showMessage('error', 'Auto-match failed');
    } finally {
      setAutoMatchLoading(false);
    }
  };

  const openMatchDialog = async (txn) => {
    const isIncome = parseFloat(txn.amount) < 0;
    setMatchDialog({ txn, type: isIncome ? 'payment' : 'expense' });
    setSuggestionsLoading(true);
    try {
      const res = await axiosInstance.get(`/reconciliation/suggestions/${txn.uuid}`);
      if (res.data.success) {
        setSuggestions(res.data.data.suggestions || []);
      }
    } catch (err) {
      console.error('Failed to load suggestions:', err);
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleMatch = async (targetId) => {
    if (!matchDialog) return;
    const { txn, type } = matchDialog;
    try {
      const endpoint = type === 'payment' ? '/reconciliation/match-payment' : '/reconciliation/match-expense';
      const body = type === 'payment'
        ? { transaction_id: txn.uuid, payment_id: targetId }
        : { transaction_id: txn.uuid, expense_id: targetId };

      const res = await axiosInstance.post(endpoint, body);
      if (res.data.success) {
        showMessage('success', `Matched to ${type}`);
        setMatchDialog(null);
        loadTransactions(page, pageSize);
        loadSummary();
      }
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Match failed');
    }
  };

  const handleUnmatch = async (txnId) => {
    try {
      const res = await axiosInstance.post(`/reconciliation/unmatch/${txnId}`);
      if (res.data.success) {
        showMessage('success', 'Transaction unmatched');
        loadTransactions(page, pageSize);
        loadSummary();
      }
    } catch (err) {
      showMessage('error', 'Failed to unmatch');
    }
  };

  const handleIgnore = async (txnId) => {
    try {
      const res = await axiosInstance.post(`/reconciliation/ignore/${txnId}`);
      if (res.data.success) {
        showMessage('success', 'Transaction ignored');
        loadTransactions(page, pageSize);
        loadSummary();
      }
    } catch (err) {
      showMessage('error', 'Failed to ignore');
    }
  };

  const openCreateExpense = (txn) => {
    setCreateExpenseDialog(txn);
    setSelectedCategory('');
  };

  const handleCreateExpense = async () => {
    if (!createExpenseDialog || !selectedCategory) return;
    setCreateLoading(true);
    try {
      const res = await axiosInstance.post(
        `/reconciliation/create-expense/${createExpenseDialog.uuid}`,
        { category_id: selectedCategory }
      );
      if (res.data.success) {
        showMessage('success', 'Expense created from bank transaction');
        setCreateExpenseDialog(null);
        loadTransactions(page, pageSize);
        loadSummary();
      }
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to create expense');
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Helpers ──
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
      .format(Math.abs(parseFloat(amount)));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'))
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusColor = {
    unmatched: 'warning',
    matched: 'success',
    ignored: 'default',
    created: 'info',
  };

  // ============================================
  return (
    <Box className="p-4 md:p-6 max-w-6xl mx-auto">

      {/* Header */}
      <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <Box>
          <Typography variant="h5" fontWeight={700}>Bank Reconciliation</Typography>
          <Typography variant="body2" color="text.secondary">
            Match imported bank transactions to your payments and expenses.
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => { loadTransactions(page, pageSize); loadSummary(); }}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={autoMatchLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh />}
            onClick={handleAutoMatch}
            disabled={autoMatchLoading}
            size="small"
          >
            {autoMatchLoading ? 'Matching…' : 'Auto-Match'}
          </Button>
        </Box>
      </Box>

      {/* Summary cards */}
      {summary && (
        <Box className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Unmatched', value: summary.unmatched, color: 'amber' },
            { label: 'Matched', value: summary.matched, color: 'green' },
            { label: 'Created', value: summary.created, color: 'blue' },
            { label: 'Ignored', value: summary.ignored, color: 'gray' },
            { label: 'Total', value: summary.total, color: 'slate' },
          ].map(s => (
            <div
              key={s.label}
              className={`rounded-lg border p-3 bg-${s.color}-50 border-${s.color}-200`}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{s.label}</p>
              <p className="text-2xl font-bold mt-1">{s.value}</p>
            </div>
          ))}
        </Box>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={<TabLabel text="Unmatched" count={summary?.unmatched} color="warning" />} />
        <Tab label={<TabLabel text="Matched" count={summary?.matched} color="success" />} />
        <Tab label={<TabLabel text="Ignored" count={summary?.ignored} color="default" />} />
        <Tab label="All" />
      </Tabs>

      {/* Table */}
      {loading ? (
        <Box className="flex justify-center py-16"><CircularProgress /></Box>
      ) : transactions.length === 0 ? (
        <Paper className="p-8 text-center" variant="outlined">
          <Typography variant="h6" gutterBottom>
            {activeTab === 0 ? 'All caught up!' : 'No transactions found'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0
              ? 'All bank transactions have been matched or categorized.'
              : 'No transactions in this category.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Box className="flex items-center justify-between px-4 py-2 border-b" sx={{ borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {totalTxns} transaction{totalTxns !== 1 ? 's' : ''}
            </Typography>
            <Box className="flex items-center gap-2">
              <Typography variant="body2" color="text.secondary">Rows:</Typography>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                className="border rounded px-2 py-1 text-sm bg-white"
              >
                {[10, 25, 50].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Box>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Matched To</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map(txn => {
                const isIncome = parseFloat(txn.amount) < 0;
                const matchedTo = txn.matchedPayment
                  ? `Payment #${txn.matchedPayment.payment_number || '—'} (${formatCurrency(txn.matchedPayment.amount)})`
                  : txn.matchedExpense
                    ? `Expense: ${txn.matchedExpense.description || '—'} (${formatCurrency(txn.matchedExpense.amount)})`
                    : null;

                return (
                  <TableRow key={txn.uuid} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={13}>
                        {formatDate(txn.date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {txn.merchant_name || txn.name}
                      </Typography>
                      {txn.merchant_name && txn.name !== txn.merchant_name && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {txn.name}
                        </Typography>
                      )}
                      <Chip
                        label={isIncome ? 'Income' : 'Expense'}
                        size="small"
                        color={isIncome ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20, mt: 0.5 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontFamily="monospace"
                        color={isIncome ? 'success.main' : 'error.main'}
                      >
                        {isIncome ? '+' : '-'}{formatCurrency(txn.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={txn.reconciliation_status}
                        size="small"
                        color={statusColor[txn.reconciliation_status] || 'default'}
                        sx={{ fontSize: 11, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {matchedTo ? (
                        <Typography variant="caption" color="text.secondary">
                          {matchedTo}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box className="flex justify-end gap-1">
                        {txn.reconciliation_status === 'unmatched' && (
                          <>
                            <Tooltip title="Match to existing record">
                              <IconButton size="small" color="primary" onClick={() => openMatchDialog(txn)}>
                                <CompareArrows fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isIncome && (
                              <Tooltip title="Create expense from this">
                                <IconButton size="small" color="info" onClick={() => openCreateExpense(txn)}>
                                  <Add fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Ignore">
                              <IconButton size="small" color="default" onClick={() => handleIgnore(txn.uuid)}>
                                <VisibilityOff fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {(txn.reconciliation_status === 'matched' || txn.reconciliation_status === 'created') && (
                          <Tooltip title="Unmatch">
                            <IconButton size="small" color="warning" onClick={() => handleUnmatch(txn.uuid)}>
                              <LinkOff fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {txn.reconciliation_status === 'ignored' && (
                          <Tooltip title="Unmatch (restore)">
                            <IconButton size="small" color="primary" onClick={() => handleUnmatch(txn.uuid)}>
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Box className="flex items-center justify-between px-4 py-3 border-t" sx={{ borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {totalTxns === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalTxns)} of {totalTxns}
            </Typography>
            <Box className="flex items-center gap-1">
              <Button size="small" disabled={page === 0} onClick={() => setPage(0)}>First</Button>
              <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Chip label={`${page + 1} / ${Math.max(1, Math.ceil(totalTxns / pageSize))}`} size="small" variant="outlined" sx={{ mx: 1 }} />
              <Button size="small" disabled={(page + 1) * pageSize >= totalTxns} onClick={() => setPage(p => p + 1)}>Next</Button>
              <Button size="small" disabled={(page + 1) * pageSize >= totalTxns} onClick={() => setPage(Math.ceil(totalTxns / pageSize) - 1)}>Last</Button>
            </Box>
          </Box>
        </TableContainer>
      )}

      {/* ── Match Dialog ── */}
      <Dialog open={!!matchDialog} onClose={() => setMatchDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Match to {matchDialog?.type === 'payment' ? 'Payment' : 'Expense'}
        </DialogTitle>
        <DialogContent>
          {matchDialog && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{matchDialog.txn.merchant_name || matchDialog.txn.name}</strong>
              {' — '}
              {formatCurrency(matchDialog.txn.amount)} on {formatDate(matchDialog.txn.date)}
            </Alert>
          )}

          {suggestionsLoading ? (
            <Box className="flex justify-center py-8"><CircularProgress /></Box>
          ) : suggestions.length === 0 ? (
            <Typography color="text.secondary" className="py-4 text-center">
              No matching {matchDialog?.type}s found within ±7 days and ±10% amount.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>{matchDialog?.type === 'payment' ? 'Method' : 'Description'}</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suggestions.map(item => (
                  <TableRow key={item.uuid} hover>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell>
                      {matchDialog?.type === 'payment'
                        ? (item.method || '—') + (item.notes ? ` — ${item.notes}` : '')
                        : item.description || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckCircle />}
                        onClick={() => handleMatch(item.uuid)}
                      >
                        Match
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchDialog(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Expense Dialog ── */}
      <Dialog open={!!createExpenseDialog} onClose={() => setCreateExpenseDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Expense from Bank Transaction</DialogTitle>
        <DialogContent>
          {createExpenseDialog && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{createExpenseDialog.merchant_name || createExpenseDialog.name}</strong>
                <br />
                {formatCurrency(createExpenseDialog.amount)} on {formatDate(createExpenseDialog.date)}
              </Alert>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Expense Category (Account)</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Expense Category (Account)"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {expenseCategories.map(cat => (
                    <MenuItem key={cat.uuid} value={cat.uuid}>
                      {cat.code} — {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateExpenseDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateExpense}
            disabled={!selectedCategory || createLoading}
            startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <Add />}
          >
            Create Expense
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Helper component ──
function TabLabel({ text, count, color = 'default' }) {
  return (
    <Box className="flex items-center gap-2">
      {text}
      {count > 0 && (
        <Chip label={count} size="small" color={color} sx={{ height: 20, fontSize: 12 }} />
      )}
    </Box>
  );
}
